import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const AdSenseSettings = () => {
  const [settings, setSettings] = useState({
    publisherId: '',
    isEnabled: false,
    autoAdsEnabled: false,
    adSlots: {
      homePageTop: { enabled: false, slotId: '', format: 'auto' },
      homePageSidebar: { enabled: false, slotId: '', format: 'auto' },
      homePageBottom: { enabled: false, slotId: '', format: 'auto' },
      inFeed: { enabled: false, slotId: '', format: 'fluid' },
      inArticle: { enabled: false, slotId: '', format: 'auto' }
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/adsense/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching AdSense settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Validate Publisher ID format
      if (settings.isEnabled && settings.publisherId && !settings.publisherId.startsWith('ca-pub-')) {
        setError('Publisher ID must start with "ca-pub-"');
        setSaving(false);
        return;
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/adsense/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(response.data);
      setSuccess('AdSense settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAdSlotChange = (slotName, field, value) => {
    setSettings(prev => ({
      ...prev,
      adSlots: {
        ...prev.adSlots,
        [slotName]: {
          ...prev.adSlots[slotName],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const adSlotNames = [
    { key: 'homePageTop', label: 'Home Page Top Banner', icon: '📱' },
    { key: 'homePageSidebar', label: 'Home Page Sidebar', icon: '📊' },
    { key: 'homePageBottom', label: 'Home Page Bottom Banner', icon: '📺' },
    { key: 'inFeed', label: 'In-Feed Ads', icon: '📰' },
    { key: 'inArticle', label: 'In-Article Ads', icon: '📝' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-3xl">💰</span>
            Google AdSense Settings
          </h1>
          <p className="text-gray-500 mt-1">Configure Google AdSense ads for your website</p>
        </div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          {showHelp ? '✕ Close Help' : '❓ Need Help?'}
        </button>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">📚 How to Set Up Google AdSense</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
            <li>Sign up for a Google AdSense account at <a href="https://www.google.com/adsense" target="_blank" rel="noopener noreferrer" className="underline">google.com/adsense</a></li>
            <li>Get your AdSense Publisher ID (starts with "ca-pub-")</li>
            <li>Create ad units in your AdSense dashboard</li>
            <li>Copy the Ad Slot IDs for each ad unit</li>
            <li>Paste your Publisher ID and Ad Slot IDs below</li>
            <li>Enable the ads you want to display</li>
            <li>Click "Save Settings" to activate</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800">
              <strong>⚠️ Important:</strong> Make sure to add your website URL to your AdSense account's list of approved sites.
            </p>
          </div>
        </div>
      )}

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">✅</span>
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">✕</button>
        </div>
      )}

      {/* Main Settings Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h2 className="text-lg font-semibold text-gray-900">General Settings</h2>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Enable/Disable AdSense */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable AdSense</label>
              <p className="text-xs text-gray-500 mt-1">Turn on/off all AdSense ads</p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, isEnabled: !prev.isEnabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.isEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Publisher ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AdSense Publisher ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={settings.publisherId}
              onChange={(e) => setSettings({ ...settings, publisherId: e.target.value })}
              placeholder="ca-pub-1234567890123456"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Find this in your AdSense account settings (starts with "ca-pub-")
            </p>
          </div>

          {/* Auto Ads */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable Auto Ads</label>
              <p className="text-xs text-gray-500 mt-1">Let Google automatically place ads on your site</p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, autoAdsEnabled: !prev.autoAdsEnabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.autoAdsEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.autoAdsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Ad Slots Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <h2 className="text-lg font-semibold text-gray-900">Ad Placement Settings</h2>
          <p className="text-sm text-gray-600 mt-1">Configure individual ad units for different positions</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {adSlotNames.map((slot) => (
            <div key={slot.key} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{slot.icon}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{slot.label}</h3>
                    <p className="text-xs text-gray-500">Ad slot for {slot.label.toLowerCase()}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleAdSlotChange(slot.key, 'enabled', !settings.adSlots[slot.key].enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.adSlots[slot.key].enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.adSlots[slot.key].enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {settings.adSlots[slot.key].enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pl-11">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Ad Slot ID</label>
                    <input
                      type="text"
                      value={settings.adSlots[slot.key].slotId}
                      onChange={(e) => handleAdSlotChange(slot.key, 'slotId', e.target.value)}
                      placeholder="1234567890"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Ad Format</label>
                    <select
                      value={settings.adSlots[slot.key].format}
                      onChange={(e) => handleAdSlotChange(slot.key, 'format', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="auto">Auto</option>
                      <option value="rectangle">Rectangle</option>
                      <option value="vertical">Vertical</option>
                      <option value="horizontal">Horizontal</option>
                      <option value="fluid">Fluid (Responsive)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <button
          onClick={fetchSettings}
          disabled={saving}
          className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Settings
            </>
          )}
        </button>
      </div>

      {/* Preview Notice */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🎨</span>
          <div>
            <h3 className="text-sm font-semibold text-purple-900">Ad Preview</h3>
            <p className="text-xs text-purple-700 mt-1">
              After saving, ads will appear on your website according to the enabled placements.
              It may take a few minutes for ads to start showing after configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdSenseSettings;
